import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/*
  DisplayNameModal
  ----------------
  Renders a non-dismissable, editorial first-use prompt asking the user
  what SugarSense should call her. Triggers only when:
    - user is signed in
    - display_name_set_at is null (i.e. she's never set this preference)
  Persists via PUT /api/auth/me. After save, refreshes /auth/me so the
  rest of the app picks up the new handle.
*/
export function DisplayNameModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    // Don't nag admins; assume staff has a real name set.
    if (user.role === "admin") { setOpen(false); return; }
    if (!user.display_name_set_at) {
      setValue(user.display_name || user.alias || user.name || "");
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [user]);

  const save = async () => {
    const v = value.trim();
    if (v.length < 1) { setErr("Tell me what to call you."); return; }
    if (v.length > 40) { setErr("Keep it under 40 characters."); return; }
    setBusy(true); setErr("");
    try {
      await api.put("/auth/me", { display_name: v });
      await refresh();
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save that. Try again?");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div data-testid="display-name-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(58, 42, 45, 0.55)", backdropFilter: "blur(6px)" }}>
      <div className="ss-card-premium max-w-md w-full p-7 sm:p-9 relative">
        <div className="flex items-center gap-2 text-mauve">
          <Sparkles size={14} />
          <p className="text-[10px] tracking-[0.4em] uppercase">Before we begin</p>
        </div>
        <h2 className="mt-3 font-heading text-3xl text-cocoa leading-tight">
          What should I call you?
        </h2>
        <p className="mt-3 text-cocoa/70 text-sm leading-relaxed">
          Just for our conversations — never shared, never shown anywhere public.
          A first name, an alias, a private nickname — whatever feels like <em>you</em>.
        </p>
        <input
          data-testid="display-name-modal-input"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          maxLength={40}
          placeholder="e.g. Jane, J., Sage…"
          className="mt-5 w-full bg-ivory border border-champagne rounded-md h-12 px-4 text-cocoa text-base focus:border-mauve outline-none"
        />
        {err && <p data-testid="display-name-err" className="mt-2 text-rose text-xs">{err}</p>}
        <div className="mt-5 flex items-center justify-between gap-3">
          <p className="text-[10px] tracking-widest uppercase text-cocoa/40">Private · editable in Account</p>
          <button
            data-testid="display-name-modal-begin"
            onClick={save}
            disabled={busy}
            className="ss-pill ss-pill-primary h-11 px-7 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Begin"}
          </button>
        </div>
      </div>
    </div>
  );
}
